import {useCallback, useMemo, useRef, useState} from 'react';
import useAccessLevels from '../use-access-levels';
import useIndustryVarianceProperties from '../use-industry-variance-properties';
import AggregatedVariantsNexusSearch, {DEFAULT_AGGREGATION_FETCH_CONFIG,} from '../../../core/services/nexus-search/aggregated-variants';
import PartnerIndustryRules from '../../../core/services/partner-industry-rules';

/**
 * Encapsulates Aggregated Variants Nexus Search gate, session, and page loading for lookup results.
 *
 * @param {object} params
 * @param {string|number|null|undefined} params.industryId
 * @param {Array} params.filters
 * @param {{entries?: Array}} params.searchSchema
 * @param {boolean} [params.userAggregationEnabled=true] — when false, aggregation is off (products list user toggle)
 */
const useAggregatedVariantsNexusSearch = ({
                                              industryId,
                                              filters = [],
                                              searchSchema,
                                              userAggregationEnabled = true,
                                          }) => {
    const operatorAccessLevels = useAccessLevels();
    const [aggregationActive, setAggregationActive] = useState(false);
    const sessionRef = useRef(AggregatedVariantsNexusSearch.createEmptySession());

    const accessEnabled = Boolean(operatorAccessLevels.packages.lookup.aggregatedVariantsNexusSearch);
    const industryEnabled = useMemo(() => PartnerIndustryRules.isVarianceAggregationIndustry({id: industryId}), [industryId],);
    const aggregationSupported = accessEnabled || industryEnabled;
    const partnerOrIndustryEnabled = aggregationSupported && userAggregationEnabled;

    const {varianceProperties, loading: loadingVarianceProperties} = useIndustryVarianceProperties(industryId, aggregationSupported,);

    const facetBlocked = useMemo(
        () => AggregatedVariantsNexusSearch.shouldDisableForFilters(
            filters,
            searchSchema?.entries ?? [],
            varianceProperties,
        ),
        [filters, searchSchema?.entries, varianceProperties],
    );

    /** True while variance props are still loading for an aggregation-eligible industry/partner. */
    const aggregationGatePending = partnerOrIndustryEnabled && loadingVarianceProperties;

    const shouldAggregate = partnerOrIndustryEnabled
        && !facetBlocked
        && !loadingVarianceProperties;

    /**
     * Resets the aggregation session (e.g. when search criteria change).
     */
    const resetSession = useCallback(() => {
        sessionRef.current = AggregatedVariantsNexusSearch.createEmptySession();
        setAggregationActive(false);
    }, []);

    /**
     * Loads one aggregated UI page (fill + look-ahead).
     *
     * @param {object} params
     * @param {boolean} params.reset
     * @param {Array} [params.previousCards]
     * @param {function(number, number): Promise<{hits: Array, totalCount: number, meta?: unknown}>} params.fetchPage
     * @param {Partial<{apiPageSize: number, uiPageSize: number}>} [params.config]
     */
    const loadAggregatedPage = useCallback(async ({
                                                      reset,
                                                      previousCards = [],
                                                      fetchPage,
                                                      config,
                                                  }) => {
        const result = await AggregatedVariantsNexusSearch.fetchPage({
            reset,
            session: sessionRef.current,
            varianceProperties,
            previousCards,
            config: {
                apiPageSize: config?.apiPageSize ?? DEFAULT_AGGREGATION_FETCH_CONFIG.apiPageSize,
                uiPageSize: config?.uiPageSize ?? DEFAULT_AGGREGATION_FETCH_CONFIG.uiPageSize,
            },
            fetchPage,
        });
        sessionRef.current = result.session;
        setAggregationActive(true);
        return result;
    }, [varianceProperties]);

    /**
     * Applies an option selection to the products list updater.
     */
    const applySelection = useCallback((nextCard, setProducts) => {
        setProducts((prev) => prev.map((p) => (
            p.varietyKey === nextCard.varietyKey ? nextCard : p
        )));
    }, []);

    return {
        shouldAggregate,
        aggregationSupported,
        aggregationGatePending,
        aggregationActive,
        setAggregationActive,
        varianceProperties,
        loadingVarianceProperties,
        sessionRef,
        resetSession,
        loadAggregatedPage,
        applySelection,
        uiPageSize: DEFAULT_AGGREGATION_FETCH_CONFIG.uiPageSize,
        apiPageSize: DEFAULT_AGGREGATION_FETCH_CONFIG.apiPageSize,
    };
};

export default useAggregatedVariantsNexusSearch;
